import { useMsal } from "@azure/msal-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import TemplateSelector from "../components/TemplateSelector";
import {
    cancelReportJob,
    createReportJob,
    deleteReportJob,
    downloadReport,
    getReportCapacity,
    listMyReportJobs,
    triggerBrowserDownload,
} from "../services/pdfService";
import { useI18n } from "../i18n";
import ClientThumb from "./ClientThumb";
import "./ReportingWorkspace.css";

const ACTIVE_STATUSES = new Set(["queued", "running"]);
const TERMINAL_STATUSES = new Set(["completed", "completed_with_warnings", "failed", "expired", "cancelled", "cancelled_by_admin"]);

function formatEpochDate(epochMs) {
    if (!epochMs) {
        return "-";
    }
    const parsed = Number(epochMs);
    if (Number.isNaN(parsed)) {
        return "-";
    }
    return new Date(parsed).toLocaleDateString("fr-FR");
}

function extractFailedImages(job) {
    if (!job || job.status !== "completed_with_warnings" || !job.message) {
        return [];
    }

    const prefix = "Report generated with missing images:";
    if (!job.message.startsWith(prefix)) {
        return [];
    }

    return job.message
        .slice(prefix.length)
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
}

function translateBackendMessage(message, t) {
    if (!message) {
        return message;
    }
    if (message === "Report ready") {
        return t("report_ready");
    }

    const missingImagesPrefix = "Report generated with missing images:";
    if (message.startsWith(missingImagesPrefix)) {
        return message.replace(
            missingImagesPrefix,
            `${t("report_generated_with_missing_images")}:`
        );
    }

    return message;
}

function formatDuration(ms) {
    if (!Number.isFinite(ms) || ms <= 0) {
        return "moins d'1 min";
    }
    const totalSeconds = Math.round(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes <= 0) {
        return "moins d'1 min";
    }
    if (minutes >= 60) {
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}min` : `${hours}h`;
    }
    if (minutes < 2 && seconds > 0) {
        return `1 min`;
    }
    return `${minutes} min`;
}

function toEpochMs(value) {
    if (!value) {
        return null;
    }
    const asNumber = Number(value);
    if (Number.isFinite(asNumber) && asNumber > 0) {
        return asNumber;
    }
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function getEstimatedRemainingMs(job) {
    const progress = Number(job?.progressPercent || 0);
    if (!Number.isFinite(progress) || progress <= 0 || progress >= 100) {
        return null;
    }
    const startEpoch = toEpochMs(job?.startedAt) || toEpochMs(job?.createdAt);
    if (!startEpoch) {
        return null;
    }
    const elapsedMs = Date.now() - startEpoch;
    if (!Number.isFinite(elapsedMs) || elapsedMs <= 10_000) {
        return null;
    }
    const estimatedTotalMs = elapsedMs / (progress / 100);
    const remainingMs = estimatedTotalMs - elapsedMs;
    if (!Number.isFinite(remainingMs) || remainingMs <= 0) {
        return null;
    }
    return remainingMs;
}

function getJobStatusClass(status) {
    const normalized = String(status || "").toLowerCase();
    if (normalized === "completed") return "job-status-badge status-success";
    if (normalized === "completed_with_warnings") return "job-status-badge status-warning";
    if (normalized === "failed" || normalized === "expired" || normalized === "cancelled" || normalized === "cancelled_by_admin") {
        return "job-status-badge status-error";
    }
    if (normalized === "running") return "job-status-badge status-running";
    if (normalized === "queued") return "job-status-badge status-queued";
    return "job-status-badge";
}

const ReportingWorkspace = () => {
    const { instance, accounts } = useMsal();
    const { t } = useI18n();
    const [startDate, setStartDate] = useState(
        new Date().toISOString().split("T")[0]
    );
    const [endDate, setEndDate] = useState(
        new Date().toISOString().split("T")[0]
    );
    const [selectedTemplates, setSelectedTemplates] = useState([]);
    const [templateSelectionVersion, setTemplateSelectionVersion] = useState(0);
    const [jobs, setJobs] = useState([]);
    const [isJobsLoading, setIsJobsLoading] = useState(true);
    const [isGlobalStatsLoading, setIsGlobalStatsLoading] = useState(true);
    const [serverCapacity, setServerCapacity] = useState(null);
    const [deletingJobIds, setDeletingJobIds] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const recentlyDeletedRef = useRef(new Set());

    const getApiToken = useCallback(async () => {
        const accessToken = await instance.acquireTokenSilent({
            scopes: ["api://c247d402-5b9d-40f2-b7e6-b15a47c56f22/Api.access"],
            account: accounts[0],
        });
        return accessToken.accessToken;
    }, [accounts, instance]);

    const updateJob = useCallback((jobId, patchOrUpdater) => {
        setJobs((prev) =>
            prev.map((job) => {
                if (job.jobId !== jobId) {
                    return job;
                }
                const patch =
                    typeof patchOrUpdater === "function"
                        ? patchOrUpdater(job)
                        : patchOrUpdater;
                return { ...job, ...patch };
            })
        );
    }, []);

    const removeJobFromList = useCallback((jobId) => {
        setJobs((prev) => prev.filter((job) => job.jobId !== jobId));
    }, []);

    const handleDownloadJob = useCallback(
        async (jobId, fallbackTitle, autoTriggered = false) => {
            updateJob(jobId, (job) => ({
                isDownloading: true,
                autoDownloadTried: job.autoDownloadTried || autoTriggered,
                downloadError: null,
            }));

            try {
                const token = await getApiToken();
                const { blob, fileName } = await downloadReport(jobId, { token });
                const resolvedFileName = fileName || `${fallbackTitle || "report"}.pdf`;
                triggerBrowserDownload(blob, resolvedFileName);
                updateJob(jobId, {
                    isDownloading: false,
                    downloaded: true,
                    downloadError: null,
                    message: "PDF downloaded",
                });
            } catch (error) {
                updateJob(jobId, {
                    isDownloading: false,
                    downloaded: false,
                    downloadError: error.message,
                });
            }
        },
        [getApiToken, updateJob]
    );

    const handleTemplateSelection = useCallback((templates) => {
        setSelectedTemplates(templates);
    }, []);

    const handleCancelJob = useCallback(
        async (jobId) => {
            try {
                const token = await getApiToken();
                const cancelledJob = await cancelReportJob(jobId, { token });
                updateJob(jobId, {
                    ...cancelledJob,
                    downloadError: null,
                });
            } catch (error) {
                updateJob(jobId, {
                    message: `Cancel failed: ${error.message}`,
                });
            }
        },
        [getApiToken, updateJob]
    );

    const handleDeleteJob = useCallback(
        async (job) => {
            if (!job || !job.jobId) {
                return;
            }
            if (deletingJobIds[job.jobId]) {
                return;
            }
            const isActive = ACTIVE_STATUSES.has(job.status);
            const isBackendJob = !job.jobId.startsWith("failed-");
            setDeletingJobIds((prev) => ({ ...prev, [job.jobId]: true }));
            recentlyDeletedRef.current.add(job.jobId);

            if (isActive && isBackendJob) {
                try {
                    const token = await getApiToken();
                    await cancelReportJob(job.jobId, { token });
                } catch (error) {
                    console.warn(`Unable to cancel job ${job.jobId} before delete:`, error);
                }
            }

            if (isBackendJob) {
                try {
                    const token = await getApiToken();
                    await deleteReportJob(job.jobId, { token });
                } catch (error) {
                    console.warn(`Unable to delete job ${job.jobId} from server:`, error);
                }
            }

            window.setTimeout(() => {
                removeJobFromList(job.jobId);
                setDeletingJobIds((prev) => {
                    const next = { ...prev };
                    delete next[job.jobId];
                    return next;
                });
            }, 700);

            window.setTimeout(() => {
                recentlyDeletedRef.current.delete(job.jobId);
            }, 15_000);
        },
        [deletingJobIds, getApiToken, removeJobFromList]
    );

    const handleGenerateAllPdfs = async () => {
        const uniqueTemplates = Array.from(new Set(selectedTemplates));

        if (uniqueTemplates.length === 0) {
            console.error("No template selected.");
            return;
        }

        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);

        try {
            const token = await getApiToken();
            const [sy, sm, sd] = startDate.split("-").map(Number);
            const selectedStartDateMs = new Date(sy, sm - 1, sd, 0, 0, 0, 0).getTime();
            const [ey, em, ed] = endDate.split("-").map(Number);
            const selectedEndDateMs = new Date(ey, em - 1, ed, 23, 59, 59, 999).getTime();
            const createdJobs = await Promise.all(
                uniqueTemplates.map(async (templateRef) => {
                    try {
                        const job = await createReportJob(
                            {
                                startDate: selectedStartDateMs,
                                endDate: selectedEndDateMs,
                                templatePath: templateRef,
                                roles: accounts[0]?.idTokenClaims?.roles || [],
                            },
                            { token }
                        );

                        return {
                            ...job,
                            template: templateRef,
                            title: job.templateName || templateRef,
                            clientName: job.clientName || "Unknown",
                            startDate: job.startDate ?? selectedStartDateMs,
                            endDate: job.endDate ?? selectedEndDateMs,
                            downloaded: false,
                            isDownloading: false,
                            autoDownloadTried: false,
                            downloadError: null,
                        };
                    } catch (error) {
                        return {
                            jobId: `failed-${templateRef}`,
                            template: templateRef,
                            title: templateRef,
                            clientName: "Unknown",
                            status: "failed",
                            phase: "failed",
                            progressPercent: 0,
                            message: null,
                            error: { message: error.message },
                            startDate: selectedStartDateMs,
                            endDate: selectedEndDateMs,
                            downloaded: false,
                            isDownloading: false,
                            autoDownloadTried: true,
                            downloadError: null,
                        };
                    }
                })
            );

            setJobs((prev) => [...createdJobs, ...prev]);
            setSelectedTemplates([]);
            setTemplateSelectionVersion((prev) => prev + 1);
        } finally {
            setIsSubmitting(false);
        }
    };

    const mergeJobsFromServer = useCallback((serverJobs) => {
        const deleted = recentlyDeletedRef.current;
        setJobs((prev) => {
            const serverById = new Map(serverJobs.map((job) => [job.jobId, job]));
            const prevById = new Map(prev.map((job) => [job.jobId, job]));

            const merged = serverJobs
                .filter((job) => !deleted.has(job.jobId))
                .map((job) => {
                    const prevJob = prevById.get(job.jobId);
                    const derivedTitle = (job.templateName || "").replace(".html", "");
                    return {
                        ...job,
                        title: job.templateName || prevJob?.title || derivedTitle || "report",
                        clientName: job.clientName || prevJob?.clientName || "Unknown",
                        downloaded: prevJob?.downloaded || false,
                        isDownloading: prevJob?.isDownloading || false,
                        autoDownloadTried: true,
                        downloadError: prevJob?.downloadError || null,
                    };
                });

            const localOnly = prev.filter(
                (job) => !serverById.has(job.jobId) && !deleted.has(job.jobId)
            );

            return [...localOnly, ...merged];
        });
    }, []);

    const refreshJobsFromServer = useCallback(async ({ silent = false } = {}) => {
        if (!silent) {
            setIsJobsLoading(true);
            setIsGlobalStatsLoading(true);
        }
        try {
            const token = await getApiToken();
            const data = await listMyReportJobs({ token, limit: 500 });
            const serverJobs = Array.isArray(data.jobs) ? data.jobs : [];
            mergeJobsFromServer(serverJobs);

            try {
                const capacity = await getReportCapacity({ token });
                setServerCapacity(capacity);
            } catch (_globalError) {
                setServerCapacity(null);
            }
        } catch (_error) {
            // Keep existing UI state if refresh fails temporarily.
        } finally {
            setIsJobsLoading(false);
            setIsGlobalStatsLoading(false);
        }
    }, [getApiToken, mergeJobsFromServer]);

    useEffect(() => {
        refreshJobsFromServer();
        const interval = window.setInterval(() => refreshJobsFromServer({ silent: true }), 2500);
        return () => window.clearInterval(interval);
    }, [refreshJobsFromServer]);

    const serverLoadLabel = (() => {
        if (isGlobalStatsLoading) {
            return t("server_capacity_loading");
        }
        if (!serverCapacity) {
            return t("server_capacity_unavailable");
        }
        return `${serverCapacity.runningJobs}/${serverCapacity.maxConcurrentExecutions} ${t("server_executions_in_progress")}`;
    })();
    const capacityMax = Number(serverCapacity?.maxConcurrentExecutions || 0);
    const capacityRunning = Number(serverCapacity?.runningJobs || 0);
    const capacityRatio = capacityMax > 0 ? Math.min(capacityRunning / capacityMax, 1) : 0;
    const capacityPercent = Math.round(capacityRatio * 100);
    const capacityRingStyle = {
        background: `conic-gradient(#3db29d ${capacityPercent}%, #dbe4ef ${capacityPercent}% 100%)`,
    };

    return (
        <div className="pdf-generator">
            <div className="pdf-layout">
                <div className="config-column">
                    <div className="date-selector">
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>

                    <TemplateSelector
                        onTemplateSelect={handleTemplateSelection}
                        clearSelectionSignal={templateSelectionVersion}
                    />

                    <button
                        className="button-generate"
                        onClick={handleGenerateAllPdfs}
                        disabled={isSubmitting || selectedTemplates.length === 0}
                    >
                        {isSubmitting ? (
                            <span className="btn-loading-content">
                                <span className="button-spinner" aria-hidden="true" />
                                {t("add_in_progress")}
                            </span>
                        ) : (
                            t("add_queue")
                        )}
                    </button>
                </div>

                <div className="jobs-column">
                    <div className="jobs-header">
                        <div className="jobs-header-actions">
                            <div className="capacity-indicator" title={serverLoadLabel}>
                                <div className="capacity-ring" style={capacityRingStyle}>
                                    <div className="capacity-ring-inner">
                                        {isGlobalStatsLoading ? "..." : `${capacityPercent}%`}
                                    </div>
                                </div>
                                <div className="capacity-text">
                                        <strong>{isGlobalStatsLoading ? t("server_capacity_loading") : serverLoadLabel}</strong>
                                    {serverCapacity && (
                                        <span>
                                                {t("running")}: {serverCapacity.runningJobs} | {t("queued")}: {serverCapacity.queuedJobs} | {t("max_capacity")}: {serverCapacity.maxConcurrentExecutions}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <button
                                className="button-generate"
                                onClick={() => refreshJobsFromServer()}
                                disabled={isJobsLoading}
                            >
                                {isJobsLoading ? (
                                    <span className="btn-loading-content">
                                        <span className="button-spinner" aria-hidden="true" />
                                        {t("loading")}
                                    </span>
                                ) : (
                                    t("refresh")
                                )}
                            </button>
                        </div>
                    </div>
                    <div className="process-list">
                        {!isJobsLoading && jobs.length === 0 && <p>{t("no_execution")}</p>}
                        {jobs.map((job) => (
                            <div
                                key={job.jobId}
                                className={`process-item ${deletingJobIds[job.jobId] ? "process-item-deleting" : ""}`}
                            >
                                {(() => {
                                    const isTerminal = TERMINAL_STATUSES.has(job.status);
                                    const progressWidth = isTerminal ? "100%" : `${job.progressPercent || 0}%`;
                                    const failedImages = extractFailedImages(job);
                                    const isDeleting = Boolean(deletingJobIds[job.jobId]);
                                    const etaMs = getEstimatedRemainingMs(job);
                                    const showEta = ACTIVE_STATUSES.has(job.status) && !isDeleting;
                                    return (
                                        <>
                                            {isDeleting && <p className="delete-feedback">{t("deleting")}</p>}
                                            <div className="job-card-header">
                                                <div>
                                                    <h4>{job.title}</h4>
                                                    <p className="job-card-client">
                                                        <ClientThumb clientName={job.clientName} getApiToken={getApiToken} size={16} />
                                                        {job.clientName || "-"}
                                                    </p>
                                                </div>
                                                <div className="job-status-area">
                                                    <span className={getJobStatusClass(job.status)}>
                                                        {job.status || "-"}
                                                    </span>
                                                    {showEta && (
                                                        <p className="job-eta">
                                                            {etaMs ? `${t("eta_prefix")} ${formatDuration(etaMs)}` : t("eta_calculating")}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="job-meta-grid">
                                                <p><strong>{t("period")}:</strong> {formatEpochDate(job.startDate)} - {formatEpochDate(job.endDate)}</p>
                                                <p><strong>Job:</strong> {job.jobId}</p>
                                            </div>
                                            <p className="job-message">
                                                {translateBackendMessage(job.message, t) ||
                                                    job.error?.message ||
                                                    job.downloadError ||
                                                    t("waiting")}
                                            </p>
                                            {job.status === "completed_with_warnings" && failedImages.length > 0 && (
                                                <details className="warning-details">
                                                    <summary>Images en erreur ({failedImages.length})</summary>
                                                    <ul>
                                                        {failedImages.map((imageName) => (
                                                            <li key={imageName}>{imageName}</li>
                                                        ))}
                                                    </ul>
                                                </details>
                                            )}
                                            <div
                                                className="progress-bar"
                                                style={{
                                                    width: "100%",
                                                    backgroundColor: "#f3f3f3",
                                                    borderRadius: "5px",
                                                    overflow: "hidden",
                                                }}
                                            >
                                                <div
                                                    className="progress-bar-fill"
                                                    style={{
                                                        width: progressWidth,
                                                        backgroundColor:
                                                            job.status === "completed"
                                                                ? "#4caf50"
                                                                : job.status === "completed_with_warnings"
                                                                    ? "#ff9800"
                                                        : job.status === "failed" || job.status === "expired" || job.status === "cancelled" || job.status === "cancelled_by_admin"
                                                                        ? "#f44336"
                                                                        : "#2196f3",
                                                        height: "20px",
                                                    }}
                                                ></div>
                                            </div>
                                            <div className="job-actions">
                                                {!isTerminal && (
                                                    <button
                                                        className="button-generate"
                                                        onClick={() => handleCancelJob(job.jobId)}
                                                        disabled={isDeleting}
                                                    >
                                                        {t("cancel")}
                                                    </button>
                                                )}
                                                {job.downloadError && (
                                                    <button
                                                        className="button-generate"
                                                        onClick={() => handleDownloadJob(job.jobId, job.title)}
                                                        disabled={job.isDownloading || isDeleting}
                                                    >
                                                        {job.isDownloading ? t("downloading") : t("retry_download")}
                                                    </button>
                                                )}
                                                {(job.status === "completed" || job.status === "completed_with_warnings") && (
                                                    <button
                                                        className="button-eye"
                                                        onClick={() => handleDownloadJob(job.jobId, job.title)}
                                                        disabled={job.isDownloading || isDeleting}
                                                        title={t("download_pdf")}
                                                    >
                                                        {job.isDownloading ? <span className="eye-spinner" /> : <span className="eye-icon">⬇</span>}
                                                    </button>
                                                )}
                                                <button
                                                    className="button-delete"
                                                    onClick={() => handleDeleteJob(job)}
                                                    disabled={isDeleting}
                                                >
                                                    {isDeleting ? t("deleting") : t("delete")}
                                                </button>
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReportingWorkspace;
